import { useState, useEffect } from 'react'

import { apple, fb, kakao, line, google } from '../utils/oauth-adapters'

import { useRecaptchaPage } from '@/shared/libs/recaptcha/context'
import { useLocales } from '@/shared/hooks/useLocales'
import { setReturnUrlCookie, setReturnUrlAfterSignUpCookie, getCurrentUrl } from '@/shared/utils/url-utils'

import type { SignInTargetType, UseSocialSignInConfig, UseSocialSignInReturn } from '../types'

export type { UseSocialSignInConfig, UseSocialSignInReturn }

const RECAPTCHA_ACTION = 'sns_login'

export const useSocialSignIn = (config: UseSocialSignInConfig = {}): UseSocialSignInReturn => {
  const { redirectUrl = '/', redirectUrlAfterSignUp, enableRecaptcha = true, onBeforeSignIn } = config

  const { countryPathnamePrefix } = useLocales()
  const [isLoading, setIsLoading] = useState(false)
  const { executeRecaptcha } = useRecaptchaPage()

  // 리다이렉트 URL 쿠키 설정
  useEffect(() => {
    if (redirectUrlAfterSignUp) {
      setReturnUrlAfterSignUpCookie(redirectUrlAfterSignUp, 1 * 24 * 60 * 60)
    }
  }, [redirectUrlAfterSignUp])

  const signIn = async (provider: SignInTargetType): Promise<void> => {
    try {
      setIsLoading(true)

      // 사전 검증 콜백 실행
      if (onBeforeSignIn) {
        const shouldProceed = await onBeforeSignIn(provider)
        if (!shouldProceed) {
          setIsLoading(false)
          return
        }
      }

      // reCAPTCHA 처리 (활성화된 경우)
      if (enableRecaptcha) {
        const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION)

        // OAuth 플로우 중 서버에서 접근할 수 있도록 쿠키에 임시 저장 (5분)
        document.cookie = `oauth_recaptcha_token=${recaptchaToken}; Max-Age=300; Path=/; SameSite=Lax`
        document.cookie = `oauth_recaptcha_action=${RECAPTCHA_ACTION}; Max-Age=300; Path=/; SameSite=Lax`
      }

      // OAuth URL 생성 및 리다이렉트
      let loginURI: string

      switch (provider) {
        case 'kakao':
          loginURI = kakao.getLoginURI()
          break
        case 'facebook':
          loginURI = fb.getLoginURI()
          break
        case 'line':
          loginURI = line.getLoginURI()
          break
        case 'apple':
          loginURI = apple.getLoginURI()
          break
        case 'google':
          loginURI = google.getLoginURI()
          break
        case 'email':
          loginURI = `${countryPathnamePrefix}/signin/email`
          break
        default:
          throw new Error(`Unsupported provider: ${provider}`)
      }

      // 쿼리 파라미터에 return_url이 있으면 해당 값을 사용, 없으면 현재 url을 사용
      const searchParams = new URLSearchParams(location.search)
      const returnUrl = searchParams.get('return_url') || getCurrentUrl()
      setReturnUrlCookie(returnUrl, 24 * 60 * 60)

      // 리다이렉트 실행
      location.href = loginURI
    } catch (error) {
      console.error('useSocialSignIn.signIn', error)
      setIsLoading(false)
      throw error
    }
  }

  return {
    signIn,
    isLoading,
  }
}
